import {
  RECIPIENT,
  type ChatQuestion,
} from '@/karehero-app/tell-us-once/intake/content';
import { CALCULATOR_BAR } from '@/karehero-app/tell-us-once/benefits/BenefitsCalculator.content';

// Version 1.0 of the benefits calculator: the first config in the shared chat
// engine (Dynamic flows: approach, slices 3 and 5).
//
// The asks and their answers are main's, word for word
// (front-end/pwa/src/config/benefitsCalculatorV2Steps.ts), because the wording
// is fixed for legal reasons. Only what sits around them is ours: the intros,
// the acknowledgements, what we say back and the thinking labels.
//
// The person is onboarding's, so the calculator reads as the same family
// carrying on. Onboarding 4 already holds where they live, their age and when
// they need help, so those are said back to check, not asked.

const R = RECIPIENT;

// When a question shows its answers (Patterns 4.1, and the interaction
// principles in Notion): only when seeing them helps someone answer. That's
// amounts in bands, and names they might not know. Everything else they say or
// type, and the agent reads it into main's option ids. "Not sure" always works.

/**
 * One of main's questions, answered in their own words, with no options.
 * Spoken or typed, the answer shows as text, so the page shows what the agent
 * reads, like "82" or "Leeds".
 */
const say = (
  id: string,
  text: string,
  answer: string,
  extra: Partial<ChatQuestion> = {},
): ChatQuestion => ({
  id,
  ask: text,
  mode: 'say',
  answer,
  typed: true,
  ...extra,
});

/** One of main's questions, its answers attached under the ask. */
const ask = (
  id: string,
  text: string,
  options: string[],
  extra: Partial<ChatQuestion> = {},
): ChatQuestion => ({
  id,
  ask: text,
  mode: 'say',
  attachRows: {},
  asides: options.map((label) => ({ label })),
  answer: options[0],
  ...extra,
});

/**
 * Checking what we hold, trialled as a rule: three things or fewer are said in
 * the message, the way a person would say them back. More than three, and
 * they're a list in a card, which is easier to check than a long sentence.
 */
const checkHeld = (
  id: string,
  lead: string,
  facts: string[],
  extra: Partial<ChatQuestion> = {},
): ChatQuestion =>
  facts.length <= 3
    ? {
        id,
        ask: `${lead} So far you've told us they ${listed(facts)}. Is that right?`,
        mode: 'say',
        typed: true,
        answer: "Yes, that's right",
        ...extra,
      }
    : {
        id,
        says: [`${lead} Here's what you've told us so far.`],
        card: { label: 'Does this look right?', items: facts, editable: true },
        ask: '',
        mode: 'say',
        typed: true,
        answer: "Yes, that's right",
        ...extra,
      };

/** "a, b and c", the way it's said. */
const listed = (items: string[]) =>
  items.length < 2
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/** What onboarding already told us, said back. */
export const HELD = [
  'live in England',
  'are 66 or older',
  'need help day and night',
];

/** The same, after "Not quite" and "It's Wales, not England". */
export const HELD_FIXED = ['live in Wales', ...HELD.slice(1)];

const RECEIVING = [
  'Attendance Allowance',
  'PIP',
  'DLA',
  'None of these',
  'Not sure',
];

export const WORKING_OUT = `Working out what ${R} could get`;

/** How many things the results find for Margaret: RESULT_1_0's claims. */
const RESULT_COUNT = 5;

/**
 * Margaret's run through the England path, after what we hold is confirmed. Every
 * "only if" a question carries is main's: the care home question is asked
 * because they live in England, the partner's age because Margaret is 66 or
 * over, the joint income because there is a partner.
 */
export const CALCULATOR_1_0: ChatQuestion[] = [
  // One message: what this is for, again, since they may have come in from
  // Home a while after onboarding, then what we hold, said back.
  checkHeld(
    'held',
    `Let's find out what help ${R} could be entitled to.`,
    HELD,
  ),
  say(
    'care-recipient-in-nursing-home',
    `Thanks. Is ${R} living in a care or nursing home?`,
    'No',
  ),
  // Five, so behind See options. They might get more than one, so they tick
  // as many as they get and Done sends them. None of these and Not sure stand
  // alone, so they're quieter rows that answer straight away.
  {
    ...ask(
      'care-recipient-already-receiving-benefits',
      `Does ${R} already get any of these?`,
      RECEIVING,
      { answer: 'None of these', many: true },
    ),
    asides: RECEIVING.map((label) => ({
      label,
      quiet: label === 'None of these' || label === 'Not sure',
    })),
  },
  say(
    'care-recipient-has-unpaid-carer',
    `Are you (or someone) caring for ${R} unpaid?`,
    'Yes, me',
  ),
  say(
    'care-recipient-unpaid-carer-35-hours-or-more',
    'Is that 35 hours a week or more?',
    'Yes, more like 40',
  ),
  say(
    'care-recipient-unpaid-carer-earns-under-limit',
    'Does the carer earn under £204 a week?',
    'Yes',
    // Said because the answer before was yes: a hard answer gets a short
    // line, then the next question. Not said after a no.
    { says: ["That's a lot to be doing."] },
  ),
  say(
    'care-recipient-has-partner',
    `Does ${R} have a partner or spouse?`,
    'Yes',
  ),
  say(
    'care-recipient-partner-over-state-pension-age',
    'Is their partner also 66 or over?',
    'Yes',
  ),
  ask(
    'care-recipient-savings-bracket',
    `Roughly, how much does ${R} have in savings?`,
    ['Under £16,000', '£16,000 to £23,250', 'Over £23,250'],
    { says: ['Thanks. A couple about money now. Rough is fine.'] },
  ),
  ask(
    'care-recipient-joint-weekly-income-bracket',
    'Roughly, their combined weekly income?',
    ['Under £364', '£364 to £450', 'Over £450'],
  ),
  say(
    'care-recipient-is-disabled',
    `Does ${R} have a disability or long-term condition?`,
    'Yes, dementia',
    { says: [`Thanks. Last few, about ${R}'s health.`] },
  ),
  say(
    'care-recipient-condition-duration-6-months',
    `Has ${R} had it for six months or more?`,
    'Yes, a few years now',
  ),
  say(
    'care-recipient-is-terminally-ill',
    `Does ${R} have a terminal illness?`,
    'No',
  ),
  say(
    'care-recipient-home-requires-adaptations',
    `Does ${R}'s home need adapting, like ramps, a wet room or rails?`,
    'No',
    // The last answer, and the one labelled wait: the results.
    { reading: { lines: [WORKING_OUT], ms: 2400 } },
  ),
  // The end of the conversation says what it found before the app shows it:
  // how many, and the one to start with. The button takes them to where the
  // results are kept, Profile under Assess.
  {
    id: 'results',
    // Closing the questions with a thanks, as onboarding does.
    says: ["Thanks, that's everything."],
    card: {
      label: `${R} might be able to get help with ${RESULT_COUNT} things`,
      body: 'Attendance Allowance is the one to start with. Your results are saved, so you can come back to them.',
      sentence: true,
      // As on Home's card: it is ours, not something the AI made.
      sparkle: false,
      action: 'See your results',
    },
    ask: '',
    mode: 'say',
    next: { '*': 'end' },
  },
];

/**
 * Holding nothing: nothing to check, and the three onboarding would have given us are
 * asked first. Then on as above.
 */
export const CALCULATOR_1_0_COLD: ChatQuestion[] = [
  say('care-recipient-location', `Where does ${R} live?`, 'Leeds', {
    says: [
      `Just a few quick questions, so we can check what ${R} could be entitled to.`,
    ],
  }),
  // Read as "66 or over".
  say('care-recipient-age-bracket', `How old is ${R}?`, '82'),
  say(
    'care-recipient-needs-help-time-of-day',
    `Does ${R} need help from someone through the day or night?`,
    'Both, the nights are hardest',
  ),
  ...CALCULATOR_1_0.slice(1),
];

// Every variant below is the main conversation with one branch swapped in, so
// a frame opened part way through has the same history above it as clicking
// through from the start, and clicking on from it carries on to the end.

/** Main's question, by id. */
const main = (id: string) => {
  const q = CALCULATOR_1_0.find((x) => x.id === id);
  if (!q) throw new Error(`No calculator question called ${id}`);
  return q;
};

/** Main, with the questions from `id` on replaced by `branch`, then on as main. */
const branchAt = (id: string, branch: ChatQuestion[], rejoin?: string) => {
  const from = CALCULATOR_1_0.indexOf(main(id));
  const to = rejoin
    ? CALCULATOR_1_0.indexOf(main(rejoin))
    : CALCULATOR_1_0.length;
  return [
    ...CALCULATOR_1_0.slice(0, from),
    ...branch,
    ...CALCULATOR_1_0.slice(to),
  ];
};

/**
 * "Not quite": they say the fix, we read it, and say it back again, changed.
 * Wales asks the same next question as England; Scotland would swap it for
 * personal care and nursing care.
 */
export const CALCULATOR_1_0_NOT_QUITE: ChatQuestion[] = branchAt(
  'held',
  [
    { ...main('held'), answer: 'Not quite' },
    {
      id: 'held-fix',
      ask: "What's not right?",
      mode: 'say',
      answer: "It's Wales, not England",
      typed: true,
      reading: { lines: ['Checking that'], ms: 1600 },
    },
    checkHeld('held-again', "Thanks, I've changed that.", HELD_FIXED),
  ],
  'care-recipient-in-nursing-home',
);

/**
 * More than one benefit already: they tick each one and Done sends them
 * together. Then which part of PIP, asked only because they get it.
 */
export const CALCULATOR_1_0_RECEIVING: ChatQuestion[] = branchAt(
  'care-recipient-already-receiving-benefits',
  [
    {
      ...main('care-recipient-already-receiving-benefits'),
      ticked: ['Attendance Allowance', 'PIP'],
      answer: 'Attendance Allowance and PIP',
    },
    ask('care-recipient-pip-part', `Which part of PIP does ${R} get?`, [
      'Daily living',
      'Mobility',
      'Both',
      'Not sure',
    ]),
  ],
  'care-recipient-has-unpaid-carer',
);

/**
 * Working out the results took too long, or failed. A way on, not a dead
 * end: the closing thanks, with the slow message in place of the results.
 */
export const CALCULATOR_1_0_SLOW: ChatQuestion[] = branchAt('results', [
  {
    id: 'slow',
    says: ["Thanks, that's everything."],
    ask: "This is taking longer than it should. Your answers are saved, so nothing's lost.",
    mode: 'say',
  },
]);

/**
 * The results, from the eligibility rules on the server, for Margaret's
 * answers. No amounts: the rules say whether, not how much, and a figure is a
 * promise the claim decides.
 *
 * Carer's Allowance isn't here. Main asks the carer questions but doesn't feed
 * them into the rules yet (ENG-3796).
 */
export const RESULT_1_0 = {
  title: `What ${R} might be able to get`,
  body: "From what you've told us. A claim decides the final answer.",
  claims: [
    {
      icon: 'hand-holding-heart',
      name: 'Attendance Allowance',
      description:
        'For help needed day and night. We can fill in the form with you.',
      about:
        "A weekly payment for people over State Pension age who need help with personal care, during the day, at night or both. Savings and income don't count towards it.",
      lead: true,
    },
    {
      icon: 'sterling-sign',
      name: 'Pension Credit',
      description: 'A top-up to weekly income, for a couple over 66.',
      about:
        'A top-up to weekly income for people over State Pension age on a lower income. Getting it can open the door to other help too, like with council tax and heating.',
    },
    {
      icon: 'house',
      name: 'Council Tax Reduction',
      description: 'Less council tax, on a lower income.',
      about:
        'A smaller council tax bill for people on a lower income. Each council runs its own scheme, so what you get depends on where you live.',
    },
    {
      icon: 'heart',
      name: 'Care from the council',
      description: 'Help at home, with savings under £23,250.',
      about: `The council can look at what help ${R} needs at home, and pay towards it when savings are under £23,250.`,
    },
    {
      icon: 'bus',
      name: 'Free bus pass',
      description: 'In England, from 66.',
      about:
        'Free bus travel at off-peak times anywhere in England, from State Pension age. You apply to the local council.',
    },
  ],
  // No buttons at the foot: each claim starts from its own drawer.
  start: 'Start filling it in',
};

/** Nothing came back from the rules. Still somewhere to go. */
export const RESULT_1_0_NONE = {
  title: "We couldn't find anything yet",
  body: `From what you've told us, nothing stands out. A care expert can look at ${R}'s situation with you.`,
  claims: [],
  primary: 'Talk it through with a care expert',
  secondary: 'Change my answers',
};

/** The card on Home that starts the calculator, in Home's lead card. */
export const HOME_CARD = {
  label: 'Check your eligibility',
  body: `${R} could be entitled to help with the cost of care. Find out in a few quick questions.`,
  action: 'Start',
  plain: true,
};

/**
 * The same card once the check is done and nothing has been started: the
 * results, to get back to them, from where they began.
 */
export const HOME_CARD_DONE = {
  label: 'Your results',
  body: `${R} might be able to get help with ${RESULT_COUNT} things. Attendance Allowance is the one to start with.`,
  action: 'See your results',
  plain: true,
};

/** Under it on Home, in place of the entry cards: one way to tell us. */
export const HELP_CARD = {
  action: 'Tell us what you need',
};

/**
 * The bar on every screen: the Home card's words, so tapping Check your
 * eligibility lands on a screen that says the same thing.
 */
export const BAR_1_0 = {
  ...CALCULATOR_BAR,
  title: 'Check your eligibility',
  leave: { ...CALCULATOR_BAR.leave, title: 'Leave for now?' },
};

/** Home and the results, held as placeholders while Joining up works them out. */
export const PLACEHOLDER_1_0 = {
  home: { title: 'Home', note: 'Being worked out in Joining up.' },
  results: { title: 'Results', note: 'Being worked out in Joining up.' },
};
